import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ChannelAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          FOR CREATORS
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Understanding Channel Analytics
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Channel analytics provide valuable insights into how your content performs on IndeeClips. Understanding these metrics helps you make informed decisions about your content strategy, identify what resonates with your audience, and grow your channel effectively.
        </Typography>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Accessing Your Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          To view your channel analytics, navigate to your channel page and click on the "Analytics" or "Stats" section. Here you'll find comprehensive data about your channel's performance, including views, watch time, subscriber growth, and engagement metrics.
        </Typography>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Key Metrics to Track
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Total Views:</strong> The number of times your videos have been watched. This metric shows overall reach and popularity
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Watch Time:</strong> The total amount of time viewers spend watching your content. Higher watch time indicates engaging content
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Subscriber Count:</strong> The total number of users subscribed to your channel. Track subscriber growth to measure channel expansion
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Average View Duration:</strong> How long viewers watch your videos on average. Longer durations suggest compelling content
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Engagement Rate:</strong> Likes, comments, and shares relative to views. Higher engagement indicates active viewer participation
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            <strong>Top Performing Videos:</strong> Identify which videos generate the most views and engagement to understand audience preferences
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Using Analytics to Grow Your Channel
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Identify successful content patterns:</strong> Look at your top-performing videos and analyze what they have in common (topic, length, format, thumbnail style)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Optimize upload timing:</strong> Check when your viewers are most active and schedule uploads accordingly (typically evenings for Indian audiences)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Improve retention:</strong> If average view duration is low, consider making videos more engaging in the first 30 seconds or shortening overall length
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Respond to audience preferences:</strong> Create more content similar to your best-performing videos while experimenting with new ideas
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            <strong>Track trends over time:</strong> Monitor changes in metrics week-over-week or month-over-month to identify growth trends or areas needing improvement
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Understanding Audience Demographics
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Analytics may include demographic information about your viewers, such as age ranges, geographic locations, and viewing devices. This data helps you tailor content to your core audience. For example, if most viewers are from specific Indian states, consider incorporating regional references or languages to strengthen viewer connection.
        </Typography>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Analytics Best Practices
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Check regularly:</strong> Review your analytics at least weekly to stay informed about channel performance
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Focus on trends, not individual videos:</strong> Look for patterns across multiple uploads rather than obsessing over single video performance
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Set realistic goals:</strong> Use analytics to set achievable targets for views, subscribers, or watch time
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Compare with benchmarks:</strong> Understand industry standards for your content category to contextualize your performance
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            <strong>Don't chase vanity metrics:</strong> Focus on meaningful engagement and loyal viewers rather than just view counts
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Need Help Interpreting Your Analytics?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            If you have questions about specific analytics metrics or need guidance on using data to improve your channel performance, we're here to help. Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ChannelAnalytics;
